import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Mail } from "@mui/icons-material";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import DialogTitle from "../Dialog-Title";
import Button from "../Button";
import {
  SEND_NEW_INVITATION_BUTTON_TEXT,
  SEND_NEW_INVITATION_ERROR_TEXT,
} from "./constants";
import FormSubmissionErrorAlert from "../forms/Form-Submission-Error-Alert";
import { displaySnackbarMessage } from "../../../layout/layout-slice";
import AccessLevelSelect from "../forms/Access-Level-Select";
import { USER_MANAGEMENT_ACCESS_LEVEL_LABEL_TEXT } from "../../../settings/users/constants";
import { createRequiredError } from "../../constants/validation";
import { HeavyText } from "./shared";
import { useInviteUserMutation } from "./api";

const formId = "restore-user";

const SendNewInvitation = ({
  accessLevel,
  email,
  handleClose,
  name,
  isOpen,
  onActionSuccess,
}) => {
  const dispatch = useDispatch();

  const defaultValues = {
    accessLevel,
  };

  const {
    control,
    handleSubmit,
    reset: resetForm,
  } = useForm({
    defaultValues: {
      accessLevel,
    },
    mode: "onTouched",
  });

  useEffect(() => {
    if (isOpen) {
      resetForm(defaultValues);
    }
  }, [isOpen]);

  const [unwrappedMutate, { isError, isLoading, reset }] =
    useInviteUserMutation();

  const sendNewInvitation = (values) =>
    unwrappedMutate({ email, fullName: name, ...values })
      .unwrap()
      .then(() => {
        handleClose();
        onActionSuccess?.();
        dispatch(
          displaySnackbarMessage(
            `Success. Invitation email has been sent to ${name} at ${email}.`
          )
        );
      })
      .catch(() => {});

  return (
    <Dialog onClose={handleClose} open={isOpen}>
      <DialogTitle
        handleClose={handleClose}
        title="Send New Invitation Email"
      />
      {isError && (
        <FormSubmissionErrorAlert
          errorText={SEND_NEW_INVITATION_ERROR_TEXT}
          formId={formId}
          resetMutate={reset}
        />
      )}
      <DialogContent>
        <Stack spacing={4}>
          <Typography variant="body1">
            {`Send a new invitation to `}
            <HeavyText>{name}</HeavyText>
            {` at `}
            <HeavyText>{email}</HeavyText>? If needed, you may edit details
            below.
          </Typography>
          <Controller
            control={control}
            name="accessLevel"
            render={({
              field: { onBlur, onChange, value },
              fieldState: { error },
            }) => (
              <AccessLevelSelect
                error={!!error}
                fullWidth
                helperText={error?.message}
                label={USER_MANAGEMENT_ACCESS_LEVEL_LABEL_TEXT}
                onBlur={onBlur}
                onChange={onChange}
                value={value}
              />
            )}
            rules={{
              required: createRequiredError(
                USER_MANAGEMENT_ACCESS_LEVEL_LABEL_TEXT
              ),
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <form id={formId} onSubmit={handleSubmit(sendNewInvitation)}>
          <Button
            isLoading={isLoading}
            startIcon={<Mail />}
            type="submit"
            variant="contained"
          >
            {SEND_NEW_INVITATION_BUTTON_TEXT}
          </Button>
        </form>
      </DialogActions>
    </Dialog>
  );
};

SendNewInvitation.propTypes = {
  accessLevel: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onActionSuccess: PropTypes.func,
};

SendNewInvitation.defaultProps = {
  onActionSuccess: undefined,
};

export default SendNewInvitation;
